import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../hooks/typedHooks";
import { useGetTourQuery, useGetToursQuery } from "../toursApiSlice";
import { useGetReviewsQuery } from "../../reviews/reviewsApiSlice";
import { useLazyGetCheckoutSessionQuery } from "../../bookings/bookingsApiSlice";
import { ReviewObj, TourObj } from "../../../shared/types";
import Review from "../../reviews/Review";
import MyReview from "../../reviews/MyReview";
import {
  selectCurrentUserId,
  selectLoginStatus,
  selectAuthChecked,
} from "../../auth/authSlice";
import LoadingPage from "../../../shared/LoadingPage";
import Badge from "../../../shared/design/Badge";
import Eyebrow from "../../../shared/design/Eyebrow";
import Reveal from "../../../shared/design/Reveal";
import TourCard from "../AllTours/TourCard";
import Gallery from "./Gallery";
import BookingCard from "./BookingCard";

const userImg = (file?: string) =>
  `${process.env.REACT_APP_API_URL}/img/users/${file}`;

const INCLUDED = [
  "Lodging & backcountry camps",
  "All meals & trail provisions",
  "Certified local guides",
  "Park permits & fees",
  "Airport transfers",
];
const EXCLUDED = [
  "Flights to the trailhead",
  "Travel insurance (required)",
  "Personal hiking gear",
  "Gratuities for guides",
];

const Tour = () => {
  const userId = useAppSelector(selectCurrentUserId);
  const isLoggedIn = useAppSelector(selectLoginStatus);
  const authChecked = useAppSelector(selectAuthChecked);
  const navigate = useNavigate();
  const { id: tourId } = useParams();
  const [saved, setSaved] = useState(false);

  const {
    data: tourData,
    isLoading: tourIsLoading,
    isSuccess: tourIsSuccess,
    isError: tourIsError,
  } = useGetTourQuery(`${tourId}`);

  const { data: reviewsData, isSuccess: reviewsIsSuccess } =
    useGetReviewsQuery(`${tourId}`);

  const { data: allToursData } = useGetToursQuery("");

  const [
    getCheckoutSession,
    {
      data: checkoutSessionData,
      isFetching: checkoutIsFetching,
      isError: checkoutIsError,
      error: checkoutError,
    },
  ] = useLazyGetCheckoutSessionQuery();

  useEffect(() => {
    if (checkoutSessionData) {
      window.location.href = `${checkoutSessionData.session.url}`;
    }
  }, [checkoutSessionData]);

  // Surface a checkout failure near the Book button. The backend now returns a
  // clean JSON error ({ status, message }) instead of crashing (todo #15), so
  // prefer its message; fall back for network/unknown errors. (todo #17)
  const checkoutErrorMessage = useMemo(() => {
    if (!checkoutIsError) return "";
    if (checkoutError && "data" in checkoutError) {
      const message = (checkoutError.data as { message?: string })?.message;
      if (message) return message;
    }
    return "Couldn't start checkout — please try again.";
  }, [checkoutIsError, checkoutError]);

  const onBook = (startDate: string) => {
    if (tourId) getCheckoutSession({ tourId, startDate });
    else navigate(0);
  };

  const reviews: ReviewObj[] = useMemo(
    () => (reviewsIsSuccess && reviewsData ? reviewsData.data.data : []),
    [reviewsIsSuccess, reviewsData]
  );

  // Rating distribution (5→1) from the real reviews.
  const dist = useMemo(() => {
    const counts = [0, 0, 0, 0, 0];
    reviews.forEach((r) => {
      if (r.rating >= 1 && r.rating <= 5) counts[r.rating - 1] += 1;
    });
    const total = reviews.length || 1;
    return [5, 4, 3, 2, 1].map((s) => ({
      s,
      pct: Math.round((counts[s - 1] / total) * 100),
    }));
  }, [reviews]);

  if (tourIsLoading) return <LoadingPage />;
  if (tourIsError || !tourIsSuccess)
    return (
      <p className="py-40 text-center text-hard">
        Unable to load this tour. Please refresh and try again.
      </p>
    );

  const tour: TourObj = tourData.data.data;
  const guide = tour.guides?.[0];
  const photos = [tour.imageCover, ...(tour.images ?? [])].filter(Boolean);
  const itinerary = [...(tour.locations ?? [])].sort(
    (a, b) => Number(a.day ?? 0) - Number(b.day ?? 0)
  );
  const related =
    allToursData?.data.data.filter((t) => t.id !== tour.id).slice(0, 3) ?? [];

  // Review ownership: own review is editable, others read-only, and a
  // logged-in user without a review gets the create form.
  let userReviewContent: JSX.Element | null = null;
  const otherReviews: JSX.Element[] = [];
  reviews.forEach((review) => {
    if (review.user._id === userId) {
      userReviewContent = <MyReview key={review._id} reviewData={review} />;
    } else {
      otherReviews.push(<Review key={review._id} reviewData={review} />);
    }
  });
  if (!userReviewContent && userId) {
    userReviewContent = (
      <MyReview
        key="new-review"
        reviewData={{ user: { _id: userId }, tour: tourId }}
      />
    );
  } else if (!userReviewContent && !userId) {
    userReviewContent = (
      <div className="rounded-2xl border border-line-200 bg-white p-6 text-[15px] text-muted">
        <NavLink to="/login" className="wn-link font-semibold text-secondary-400">
          Log in
        </NavLink>{" "}
        to leave a review.
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Breadcrumb + title */}
      <section className="mx-auto max-w-[1320px] px-6 pb-5 pt-8 md:px-12">
        <div className="mb-4 font-mono text-[12.5px] tracking-[0.06em] text-muted-300">
          <NavLink to="/tours" className="wn-link text-secondary-400">
            TOURS
          </NavLink>
          <span className="mx-2">/</span>
          {tour.difficulty.toUpperCase()}
          <span className="mx-2">/</span>
          <span className="text-ink">{tour.name.toUpperCase()}</span>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="mb-3 flex items-center gap-3">
              <Badge difficulty={tour.difficulty} className="!bg-hard/10">
                {tour.difficulty.toUpperCase()} · {tour.duration} DAYS
              </Badge>
              <span className="text-[14px] font-bold">
                ★ {tour.ratingsAverage}{" "}
                <span className="font-medium text-muted-300">
                  ({tour.ratingsQuantity} reviews)
                </span>
              </span>
            </div>
            <h1 className="font-serif text-[38px] font-normal leading-[1.02] tracking-[-0.02em] md:text-[54px]">
              {tour.name}
            </h1>
            <div className="mt-3.5 flex flex-wrap items-center gap-4 text-[14.5px] text-muted">
              <span>⌖ {tour.startLocation?.description}</span>
              <span>◷ {tour.duration} days</span>
              <span>⛺ Max group of {tour.maxGroupSize}</span>
            </div>
          </div>
          <div className="flex gap-2.5">
            <button
              onClick={() => setSaved((s) => !s)}
              className="flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-[14px] font-semibold text-ink"
            >
              <span className={saved ? "text-hard" : ""}>{saved ? "♥" : "♡"}</span>{" "}
              Save
            </button>
            <button className="flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-[14px] font-semibold text-ink">
              ↗ Share
            </button>
          </div>
        </div>
      </section>

      <Gallery photos={photos} name={tour.name} />

      {/* Body */}
      <section className="mx-auto grid max-w-[1320px] items-start gap-14 px-6 pb-8 pt-12 md:px-12 lg:grid-cols-[1fr_380px]">
        <div>
          {/* Guide */}
          {guide && (
            <div className="mb-7 flex items-center gap-3.5 border-b border-line pb-6">
              <img
                src={userImg(guide.photo)}
                alt={guide.name}
                crossOrigin="anonymous"
                className="h-[54px] w-[54px] rounded-full object-cover"
              />
              <div>
                <div className="text-[12.5px] tracking-[0.04em] text-muted-300">
                  LED BY
                </div>
                <div className="text-[16px] font-bold">{guide.name}</div>
                <div className="text-[13px] text-muted">Lead guide</div>
              </div>
            </div>
          )}

          {/* Intro */}
          <h2 className="mb-3.5 font-serif text-[30px] font-normal tracking-[-0.01em]">
            About this expedition
          </h2>
          <p className="mb-4 text-[16.5px] leading-relaxed text-[#3f4a43]">
            {tour.summary}
          </p>
          <p className="mb-9 text-[16.5px] leading-relaxed text-[#3f4a43]">
            {tour.description}
          </p>

          {/* Highlights (from real data) */}
          <div className="mb-11 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            {[
              { icon: "🏔️", t: `${tour.duration} days on the trail`, s: `${tour.duration - 1} nights out` },
              { icon: "⛺", t: `Max group of ${tour.maxGroupSize}`, s: "Small by design" },
              { icon: "🧭", t: `${itinerary.length} stops`, s: "Point to point" },
              { icon: "🥾", t: `${tour.difficulty} terrain`, s: "Know before you go" },
            ].map((h) => (
              <div
                key={h.t}
                className="flex gap-3.5 rounded-2xl bg-white p-[18px] capitalize"
              >
                <span className="text-[20px]">{h.icon}</span>
                <div>
                  <div className="text-[14.5px] font-bold">{h.t}</div>
                  <div className="mt-0.5 text-[13px] text-muted-400">{h.s}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Itinerary (from real locations) */}
          {itinerary.length > 0 && (
            <>
              <h2 className="mb-[18px] font-serif text-[30px] font-normal tracking-[-0.01em]">
                The itinerary
              </h2>
              <div className="mb-11 flex flex-col gap-2.5">
                {itinerary.map((loc, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 rounded-[14px] border border-line-200 bg-white px-[18px] py-4"
                  >
                    <span className="w-[26px] font-mono text-[13px] font-medium text-secondary-400">
                      {String(loc.day ?? i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif text-[19px]">
                      {loc.description}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Included / excluded (representative) */}
          <h2 className="mb-4.5 font-serif text-[30px] font-normal tracking-[-0.01em]">
            What's included
          </h2>
          <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
            <div>
              <div className="mb-3 font-mono text-[12px] tracking-[0.08em] text-easy">
                INCLUDED
              </div>
              <div className="flex flex-col gap-2.5 text-[15px] text-[#3f4a43]">
                {INCLUDED.map((x) => (
                  <div key={x} className="flex gap-2.5">
                    <span className="font-bold text-easy">✓</span>
                    {x}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-3 font-mono text-[12px] tracking-[0.08em] text-hard">
                NOT INCLUDED
              </div>
              <div className="flex flex-col gap-2.5 text-[15px] text-[#3f4a43]">
                {EXCLUDED.map((x) => (
                  <div key={x} className="flex gap-2.5">
                    <span className="font-bold text-hard">—</span>
                    {x}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <BookingCard
          tour={tour}
          onBook={onBook}
          loading={checkoutIsFetching}
          error={checkoutErrorMessage}
          isAuthenticated={isLoggedIn}
          authResolved={authChecked}
        />
      </section>

      {/* Reviews */}
      <section className="mx-auto max-w-[1320px] px-6 pb-5 pt-8 md:px-12">
        <div className="border-t border-line pt-11">
          <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
            <div>
              <Eyebrow className="mb-2.5">04 — FROM THE TRAIL</Eyebrow>
              <h2 className="font-serif text-[32px] font-normal tracking-[-0.02em] md:text-[38px]">
                What wanderers say
              </h2>
            </div>
            <div className="flex items-center gap-5">
              <div className="text-center">
                <div className="font-serif text-[44px] leading-none text-secondary-400">
                  {tour.ratingsAverage}
                </div>
                <div className="mt-1 text-[12px] text-muted-300">
                  {tour.ratingsQuantity} reviews
                </div>
              </div>
              <div className="flex min-w-[160px] flex-col gap-1">
                {dist.map((d) => (
                  <div
                    key={d.s}
                    className="flex items-center gap-2 text-[11px] text-muted-300"
                  >
                    <span className="w-2">{d.s}</span>
                    <span className="text-medium">★</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded bg-line-200">
                      <div
                        className="h-full rounded bg-secondary-400"
                        style={{ width: `${d.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-6">{userReviewContent}</div>
          {otherReviews.length ? (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {otherReviews}
            </div>
          ) : (
            <p className="text-[15px] text-muted">
              Be the first to leave a review!
            </p>
          )}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mx-auto max-w-[1320px] px-6 pb-4 pt-11 md:px-12">
          <h2 className="mb-6 font-serif text-[32px] font-normal tracking-[-0.02em]">
            You might also wander
          </h2>
          <div className="grid grid-cols-1 gap-[26px] md:grid-cols-2 lg:grid-cols-3">
            {related.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08}>
                <TourCard tourObj={t} />
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Tour;
